/**
 * The Tasks calendar's MONTH view (rebuilt 2026-10-31 round the plan
 * check; the approved `MonthView` mockup within Akash's decisions).
 *
 * A grid of the month's days on the SITE's calendar, Monday first, and a
 * sidebar beside it:
 *
 *  - CELLS say how much work a day held or holds ("12 tasks" before
 *    today, "12 planned" from today on), draw it as one thin bar by task
 *    type (solid for what happened, outlined for what is still to come),
 *    and badge what needs a look: work that didn't finish, a fire alarm
 *    or drill, and -- from today on, for the roles the plan check is for
 *    -- how many cleaning zones the plan leaves past their target. Last,
 *    "Blocked" on a day blocked time touches, for everyone: context, not
 *    something to act on (decision 11, Akash 2026-10-31).
 *  - THE SIDEBAR holds the type legend (click a type to hide it on the
 *    grid; remembered per browser), and below it either the month at a
 *    glance (the plan check's summary and the zones with no clean
 *    planned) or,
 *    once a day is chosen, that day: its counts, its busiest hours, its
 *    work by service, the zones short that day, and "Open" and "New task
 *    on this day". A past day shows what happened and no plan.
 *
 * Nothing about robot downtime, lift traffic, forecasts or money is here
 * (decision 6): there is no data behind them. The schedule list the old
 * sidebar carried is gone too; the Agenda's schedules panel has it.
 *
 * Draws what it is handed and fetches nothing (`TaskCalendar` gathers it).
 */
import {
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type DependencyList,
  type KeyboardEvent,
  type RefObject,
} from 'react';
import type { PlanCheck } from '../../../api/v1';
import { HelpNote } from '../../../help';
import type { LanePaint } from '../calendar-categories';
import { browserTimeZone, formatZonedClock, timeZoneLabel } from '../zoned-time';
import type { LanesNavigation } from './CalendarLanes';
import { DIDNT_FINISH, type LaneItem, type LaneOutcome } from './lane-items';
import { laneVars, paintLabel } from './lane-style';
import { addDays, weekdayOf, type DateKey } from './lane-time';
import {
  addMonths,
  busiestHours,
  busiestText,
  buildMonthDays,
  byService,
  cellCount,
  cellLabel,
  dayShort,
  dayTitle,
  didntFinishText,
  gapLine,
  gapsOn,
  isChecked,
  monthPaints,
  monthStartKey,
  monthTitle,
  monthWeeks,
  planDaysText,
  planSummary,
  sameMonth,
  shortByDay,
  shortZoneText,
  shortZones,
  siteBarLine,
  targetsText,
  unscheduledLine,
  zonesShortText,
  type MonthDay,
} from './month-model';
import { robotText } from './phone-model';
import type { LaneData } from './useCalendarLaneData';
import { PriorityBadge } from '../TaskPriority';
import '../TaskCalendar.css';

/** What the Month view knows about the plan check. */
export interface MonthPlanState {
  /** The viewer's role is one the plan check is for. When false nothing
   *  about the plan is drawn (and nothing was asked for). */
  shown: boolean;
  data?: PlanCheck;
  loading: boolean;
  /** Why it could not be read, in words; null when it was. */
  error: string | null;
  /** Offered with an error only when asking again can help: a server
   *  fault or no connection, not a refusal or a server without it. */
  onRetry?: () => void;
  /** Why it is shown but was not asked for, when that is no failure:
   *  under "View as" a role the plan check is for, by someone whose own
   *  role it is not (`usePlanCheckAccess`). */
  note?: string | null;
}

export interface MonthViewProps {
  dateKey: DateKey;
  todayKey: DateKey;
  timeZone: string;
  nowMs: number;
  data: LaneData;
  /** The month's blocked times could not be read. Said, because a day
   *  with no "Blocked" badge then says nothing (decision 11), and the runs
   *  a blocked time holds are counted as planned while the plan check
   *  leaves them out (review, 2026-10-31). */
  blockedTimesFailed?: boolean;
  plan: MonthPlanState;
  hidden: ReadonlySet<LanePaint>;
  onToggleType: (paint: LanePaint) => void;
  onNavigate: (patch: LanesNavigation) => void;
  /** The toolbar's "+ New task". Withheld for a role that may not. */
  onNewTask?: () => void;
  /** "New task on this day", with the date filled in. Withheld for a
   *  role that may not create tasks. */
  onNewTaskOnDay?: (key: DateKey) => void;
}

const WEEK_HEAD = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;

/** A job not done yet: planned, waiting in a queue, or under way. */
const STILL_TO_DO: ReadonlySet<LaneOutcome> = new Set<LaneOutcome>([
  'scheduled',
  'queued',
  'running',
  'attention',
  'paused',
]);

export function MonthView(props: MonthViewProps) {
  const { dateKey, todayKey, timeZone, nowMs, data, plan, hidden, onNavigate } = props;
  const [selected, setSelected] = useState<DateKey | null>(null);
  const [announce, setAnnounce] = useState('');
  // The zones with no clean planned, listed when asked (decision 2).
  const [unscheduledOpen, setUnscheduledOpen] = useState(false);
  const cellRefs = useRef(new Map<DateKey, HTMLButtonElement>());
  const unscheduledRef = useRef<HTMLButtonElement>(null);

  // A day belongs to its month: paging away closes it.
  const day = selected !== null && sameMonth(selected, dateKey) ? selected : null;

  const weeks = useMemo(() => monthWeeks(dateKey), [dateKey]);
  const days = useMemo(
    () =>
      buildMonthDays({
        monthKey: dateKey,
        todayKey,
        timeZone,
        items: data.items,
        siteBars: data.siteBars,
        hidden,
      }),
    [dateKey, todayKey, timeZone, data.items, data.siteBars, hidden],
  );
  const paints = useMemo(() => monthPaints(data.items, hidden), [data.items, hidden]);
  const planData = plan.shown ? plan.data : undefined;
  const shortDays = useMemo(() => shortByDay(planData), [planData]);
  const gridRef = useRef<HTMLDivElement>(null);
  useCompactBadges(gridRef, [days, shortDays]);

  // Keyboard: one cell in the tab order -- the last one focused, else the
  // chosen day, else today, else the 1st -- and the arrows move between
  // days, so Tab leaves the grid in one step and comes back where it was.
  const [lastFocused, setLastFocused] = useState<DateKey | null>(null);
  const focusKey =
    (lastFocused !== null && sameMonth(lastFocused, dateKey) ? lastFocused : null) ??
    day ??
    (sameMonth(todayKey, dateKey) ? todayKey : monthStartKey(dateKey));

  const choose = (key: DateKey) => {
    setSelected(key);
    const d = days.get(key);
    if (d) {
      setAnnounce(
        `${cellLabel(d, shortDays.get(key) ?? null)}. Its details are beside the calendar.`,
      );
    }
  };
  const close = () => {
    const was = day;
    setSelected(null);
    setAnnounce('Showing the month.');
    if (was) cellRefs.current.get(was)?.focus();
  };
  const showUnscheduled = () => {
    setSelected(null);
    setUnscheduledOpen(true);
    // After the summary has replaced the day.
    window.setTimeout(() => unscheduledRef.current?.focus(), 0);
  };

  const onCellKey = (e: KeyboardEvent<HTMLButtonElement>, key: DateKey) => {
    const step: Record<string, number> = {
      ArrowLeft: -1,
      ArrowRight: 1,
      ArrowUp: -7,
      ArrowDown: 7,
    };
    let target: DateKey | null = null;
    if (e.key in step) target = addDays(key, step[e.key]!);
    else if (e.key === 'Home') target = addDays(key, -((weekdayOf(key) + 6) % 7));
    else if (e.key === 'End') target = addDays(key, 6 - ((weekdayOf(key) + 6) % 7));
    else if (e.key === 'Escape' && day) {
      e.preventDefault();
      close();
      return;
    }
    if (target === null) return;
    e.preventDefault();
    // The grid stays on its month; paging is the arrows above it.
    cellRefs.current.get(target)?.focus();
  };

  const isCurrentMonth = sameMonth(dateKey, todayKey);
  const browserZone = browserTimeZone();
  const chosen = day ? days.get(day) : undefined;

  const viewTabs = (['day', 'week', 'month', 'agenda'] as const).map((v) => (
    <button
      key={v}
      type="button"
      role="tab"
      aria-selected={v === 'month'}
      className={`nx-lanes__tab${v === 'month' ? ' is-active' : ''}`}
      onClick={() => onNavigate({ cal: v })}
      data-testid={`task-cal-view-${v}`}
    >
      {v[0]!.toUpperCase() + v.slice(1)}
    </button>
  ));

  return (
    <section
      className="nx-lanes nx-month"
      data-testid="task-calendar"
      data-view="month"
      aria-label="Task calendar"
    >
      <div className="nx-lanes__toolbar">
        <div className="nx-lanes__tabs" role="tablist" aria-label="Calendar view">
          {viewTabs}
        </div>
        <div className="nx-lanes__nav">
          <button
            type="button"
            className="nx-lane-btn nx-lane-btn--icon"
            aria-label="Previous month"
            onClick={() => onNavigate({ date: addMonths(dateKey, -1) })}
            data-testid="task-cal-prev"
          >
            ‹
          </button>
          <span className="nx-lanes__cursor" data-testid="task-cal-cursor" aria-live="polite">
            {monthTitle(dateKey)}
          </span>
          <button
            type="button"
            className="nx-lane-btn nx-lane-btn--icon"
            aria-label="Next month"
            onClick={() => onNavigate({ date: addMonths(dateKey, 1) })}
            data-testid="task-cal-next"
          >
            ›
          </button>
          <button
            type="button"
            className="nx-lane-btn"
            disabled={isCurrentMonth}
            onClick={() => onNavigate({ date: null })}
            data-testid="task-cal-today"
          >
            This month
          </button>
        </div>
        <div className="nx-lanes__actions">
          {props.onNewTask && (
            <button
              type="button"
              className="nx-lane-btn nx-lane-btn--primary"
              onClick={props.onNewTask}
              data-testid="cal-new-task"
            >
              + New task
            </button>
          )}
        </div>
      </div>

      <div className="nx-lanes__meta">
        <span data-testid="task-cal-zone" className={browserZone === timeZone ? '' : 'is-differs'}>
          {browserZone === timeZone
            ? `Times in ${timeZoneLabel(timeZone, nowMs)}`
            : `Times are the site’s, ${timeZoneLabel(timeZone, nowMs)} — your clock is ${timeZoneLabel(browserZone, nowMs)}`}
          {isCurrentMonth ? ` · now ${formatZonedClock(nowMs, timeZone)}` : ''}
        </span>
        {data.historyLoading && (
          <span data-testid="cal-history-loading">Loading finished tasks…</span>
        )}
        {data.historyError && (
          <span className="is-warn" data-testid="cal-history-error">
            Finished tasks for this month could not be loaded — past work may be missing.
          </span>
        )}
        {props.blockedTimesFailed && (
          <span className="is-warn" data-testid="cal-blocked-error">
            Blocked times for this month could not be loaded — a blocked day may show no badge and
            count runs that will not go out.
          </span>
        )}
        {data.historyPartial && (
          <span className="is-warn" data-testid="cal-history-partial">
            Only part of this month’s finished tasks loaded — open a day to see all of it.
          </span>
        )}
        {data.fireHistoryMissing && (
          <span data-testid="cal-fire-current-only">
            Past fire events are not available from this server.
          </span>
        )}
      </div>

      <p className="nx-lanes__sr" aria-live="polite" data-testid="month-announce">
        {announce}
      </p>

      <div className="nx-month__body">
        <div className="nx-month__grid-wrap">
          <div className="nx-month__head" aria-hidden="true">
            {WEEK_HEAD.map((d) => (
              <span key={d} className="nx-month__head-day">
                {d}
              </span>
            ))}
          </div>
          <div
            className="nx-month__grid"
            role="group"
            aria-label={`${monthTitle(dateKey)}, one button per day`}
            style={{ ['--nx-month-weeks' as string]: weeks.length }}
            ref={gridRef}
            data-testid="month-grid"
          >
            {weeks.flat().map((key) => {
              const d = days.get(key);
              if (!d) {
                return <div key={key} className="nx-month__cell is-out" aria-hidden="true" />;
              }
              const short = shortDays.get(key) ?? null;
              return (
                <MonthCell
                  key={key}
                  day={d}
                  shortZones={short}
                  selected={key === day}
                  tabbable={key === focusKey}
                  onChoose={() => choose(key)}
                  onFocus={() => setLastFocused(key)}
                  onKeyDown={(e) => onCellKey(e, key)}
                  cellRef={(el) => {
                    if (el) cellRefs.current.set(key, el);
                    else cellRefs.current.delete(key);
                  }}
                />
              );
            })}
          </div>
        </div>

        <aside className="nx-month__aside" data-testid="month-aside">
          <Legend paints={paints} hidden={hidden} onToggle={props.onToggleType} />
          {chosen ? (
            <DayPanel
              key={chosen.key}
              day={chosen}
              todayKey={todayKey}
              timeZone={timeZone}
              robots={data.robots}
              plan={plan}
              hidden={hidden}
              onClose={close}
              onOpenDay={() => onNavigate({ cal: 'day', date: chosen.key })}
              onNewTaskOnDay={
                props.onNewTaskOnDay && chosen.kind !== 'past'
                  ? () => props.onNewTaskOnDay!(chosen.key)
                  : undefined
              }
              onShowUnscheduled={showUnscheduled}
            />
          ) : (
            <MonthSummary
              monthKey={dateKey}
              timeZone={timeZone}
              days={days}
              plan={plan}
              unscheduledOpen={unscheduledOpen}
              onToggleUnscheduled={() => setUnscheduledOpen((o) => !o)}
              unscheduledRef={unscheduledRef}
              onChooseDay={(key) => {
                choose(key);
                cellRefs.current.get(key)?.focus();
              }}
            />
          )}
        </aside>
      </div>
    </section>
  );
}

// ---- a day of the grid ------------------------------------------------------------

function MonthCell({
  day,
  shortZones,
  selected,
  tabbable,
  onChoose,
  onFocus,
  onKeyDown,
  cellRef,
}: {
  day: MonthDay;
  shortZones: number | null;
  selected: boolean;
  tabbable: boolean;
  onChoose: () => void;
  onFocus: () => void;
  onKeyDown: (e: KeyboardEvent<HTMLButtonElement>) => void;
  cellRef: (el: HTMLButtonElement | null) => void;
}) {
  const happened = day.segments.filter((s) => s.happened > 0);
  const toCome = day.segments.filter((s) => s.toCome > 0);
  const pct = (n: number) => `${(n / day.total) * 100}%`;
  return (
    <button
      ref={cellRef}
      type="button"
      className={`nx-month__cell is-${day.kind}${selected ? ' is-selected' : ''}`}
      aria-label={cellLabel(day, shortZones)}
      aria-pressed={selected}
      aria-current={day.kind === 'today' ? 'date' : undefined}
      tabIndex={tabbable ? 0 : -1}
      onClick={onChoose}
      onFocus={onFocus}
      onKeyDown={onKeyDown}
      data-testid={`cal-month-day-${day.key}`}
    >
      <span className="nx-month__cell-top" aria-hidden="true">
        <span className="nx-month__num">{Number(day.key.slice(8))}</span>
        <span className="nx-month__count" data-testid={`cal-month-count-${day.key}`}>
          {cellCount(day)}
        </span>
      </span>
      {day.total > 0 && (
        <span className="nx-month__bar" aria-hidden="true" data-testid={`cal-month-bar-${day.key}`}>
          {happened.map((s) => (
            <span
              key={`h-${s.paint}`}
              className="nx-month__seg"
              data-paint={s.paint}
              style={{ ...laneVars(s.paint), width: pct(s.happened) }}
            />
          ))}
          {toCome.map((s) => (
            <span
              key={`p-${s.paint}`}
              className="nx-month__seg is-to-come"
              data-paint={s.paint}
              style={{ ...laneVars(s.paint), width: pct(s.toCome) }}
            />
          ))}
        </span>
      )}
      {/* The plan check's badge first: only today's cell can carry all
          of them, and in a six-week month at 1280x720 the last one was cut
          off or not drawn at all (review, 2026-10-31). This order also
          packs into two lines once they go short (`useCompactBadges`):
          "12 short · Drill", then "1 unfinished +1" -- in full, "1 didn't
          finish" left no room for "+1" beside it, so it went into "+2"
          with Blocked (decision 13, Akash 2026-10-31).
          Blocked last: it explains a quiet day rather than asking for a
          look (decision 11), so it is the first to go into "+N" when they
          do not fit. It has no shorter form: "Blocked" is one word
          already. */}
      <span className="nx-month__badges" aria-hidden="true">
        {shortZones ? (
          <span
            className="nx-month__badge is-short"
            data-short={`${shortZones} short`}
            data-testid={`cal-month-short-${day.key}`}
          >
            <span className="nx-month__badge-text">{zonesShortText(shortZones)}</span>
          </span>
        ) : null}
        {day.fire && (
          <span
            className="nx-month__badge is-fire"
            data-short={day.fire.replace(/^Fire /, '').replace(/^./, (c) => c.toUpperCase())}
            data-testid={`cal-month-fire-${day.key}`}
          >
            <span className="nx-month__badge-text">{day.fire}</span>
          </span>
        )}
        {day.didntFinish > 0 && (
          <span
            className="nx-month__badge is-fail"
            data-short={`${day.didntFinish} unfinished`}
            data-testid={`cal-month-fail-${day.key}`}
          >
            <span className="nx-month__badge-text">{didntFinishText(day.didntFinish)}</span>
          </span>
        )}
        {day.blocked && (
          <span className="nx-month__badge is-blocked" data-testid={`cal-month-blocked-${day.key}`}>
            <span className="nx-month__badge-text">Blocked</span>
          </span>
        )}
      </span>
    </button>
  );
}

/**
 * A cell whose badges do not fit says them shorter -- "2 short", "Drill",
 * "1 unfinished" (decision 13, Akash 2026-10-31) -- rather than lose one
 * off its bottom edge (`data-short`, drawn by the stylesheet; the cell's
 * own label and the row's hover title still say them in full). Still not
 * fitting, the last ones go into "+N" one at a time (`data-over`), down to
 * the first alone and "+N" on one line (`data-first-only`) -- a short cell
 * under the "Dispatch unavailable" banner at 1280x720 has room for one
 * line -- so none goes without a sign (review, 2026-10-31: the last badge,
 * the plan check's, was cut in half or not drawn). One at a time because
 * Blocked made today's cell four badges: dropping all but the first hid
 * the fire drill and what didn't finish, which had fitted, behind "+3"
 * (decision 11, gate 2026-10-31); now Blocked, last, goes first.
 *
 * "+N" goes after the last badge shown, and when it does not fit there --
 * in a narrow cell even "1 unfinished" fills a line of its own -- beside
 * the first, before another badge is given up (`data-more-beside-first`):
 * on today, Blocked going into "+1" pushed "+1" onto a third line, and the
 * loop then hid what didn't finish as well, which had fitted (review,
 * 2026-10-31).
 * A compacted row says all its badges in full on hover (`title`; the row
 * is hidden from screen readers, which read the cell's label), so a mouse
 * can tell what "+1" holds without opening the day.
 *
 * A badge cut at its side counts as not fitting, as one off the bottom
 * does (`cut`): on one line the first shrinks to leave room for "+N", and
 * a service operator's cell under the banner read "2 unfinish… +1"
 * (review, 2026-10-31). The first still cut when nothing more can go --
 * "10 unfinished +1" on one line at 1280x720 -- gives up most of its side
 * padding (`data-tight`) rather than its words.
 *
 * Measured, because whether they fit depends on the cell's size, which the
 * month's weeks and the screen decide, and on the words, which the day
 * does. Again whenever the grid changes size.
 */
function useCompactBadges(gridRef: RefObject<HTMLElement>, deps: DependencyList) {
  useLayoutEffect(() => {
    const grid = gridRef.current;
    if (!grid) return undefined;
    const tooTall = (box: HTMLElement) => box.scrollHeight > box.clientHeight + 1;
    // Its words end in "…": wider than the cell on a line of its own, or
    // the first beside "+N" on one line.
    const cut = (b: Element) => b.scrollWidth > b.clientWidth;
    // Giving the last badges up cannot widen the first, so only the
    // others being cut asks for another to go.
    const over = (box: HTMLElement, badges: Element[]) => tooTall(box) || badges.slice(1).some(cut);
    const fit = () => {
      for (const box of grid.querySelectorAll<HTMLElement>('.nx-month__badges')) {
        const badges = Array.from(box.children);
        box.removeAttribute('data-compact');
        box.removeAttribute('data-more');
        box.removeAttribute('data-more-beside-first');
        box.removeAttribute('data-first-only');
        box.removeAttribute('data-tight');
        box.removeAttribute('title');
        for (const b of badges) b.removeAttribute('data-over');
        if (!tooTall(box) && !badges.some(cut)) continue;
        box.setAttribute('data-compact', '');
        box.title = badges.map((b) => b.textContent ?? '').join(', ');
        for (let shown = badges.length - 1; shown >= 1 && over(box, badges); shown -= 1) {
          badges[shown]!.setAttribute('data-over', '');
          box.setAttribute('data-more', `+${badges.length - shown}`);
          if (shown === 1) {
            box.setAttribute('data-first-only', '');
          } else if (over(box, badges)) {
            box.setAttribute('data-more-beside-first', '');
            if (over(box, badges)) box.removeAttribute('data-more-beside-first');
          }
        }
        if (badges[0] && cut(badges[0])) box.setAttribute('data-tight', '');
      }
    };
    fit();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(fit);
    ro.observe(grid);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

interface ScrollEdges {
  /** Scrolled down: there is more above. */
  above: boolean;
  /** There is more below. */
  below: boolean;
}

/** Whether `ref` has more to scroll to above and below what it shows: the
 *  sidebar fades that edge then (`scrollFadeClass`). Below, because at
 *  1280x720 the plan check could sit wholly below the fold with nothing to
 *  say so (review, 2026-10-31). Above, because opening the zones with no
 *  clean planned at 1440x800 scrolls the month's summary to its end, and
 *  the line left half under the legend read as cut off, not scrolled
 *  (gate, 2026-10-31). */
function useScrollEdges(ref: RefObject<HTMLElement>, deps: DependencyList): ScrollEdges {
  const [edges, setEdges] = useState<ScrollEdges>({ above: false, below: false });
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const check = () => {
      const above = el.scrollTop > 2;
      const below = el.scrollHeight - el.scrollTop - el.clientHeight > 2;
      setEdges((was) => (was.above === above && was.below === below ? was : { above, below }));
    };
    check();
    el.addEventListener('scroll', check, { passive: true });
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(check);
    ro?.observe(el);
    for (const child of Array.from(el.children)) ro?.observe(child);
    return () => {
      el.removeEventListener('scroll', check);
      ro?.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return edges;
}

/** The classes that fade a scroll area's edges, from `useScrollEdges`. */
function scrollFadeClass(edges: ScrollEdges): string {
  return `${edges.above ? ' has-above' : ''}${edges.below ? ' has-more' : ''}`;
}

// ---- the legend ------------------------------------------------------------------

/** Click a type to hide it on the grid, again to show it (decision 7).
 *  `aria-pressed` is "shown". */
function Legend({
  paints,
  hidden,
  onToggle,
}: {
  paints: readonly LanePaint[];
  hidden: ReadonlySet<LanePaint>;
  onToggle: (paint: LanePaint) => void;
}) {
  const headingId = useId();
  return (
    <div className="nx-month__legend-wrap">
      <h3 className="nx-month__h" id={headingId}>
        Show on the calendar
      </h3>
      {paints.length === 0 ? (
        <p className="nx-month__quiet" data-testid="month-legend-empty">
          Nothing on the calendar this month.
        </p>
      ) : (
        <div
          className="nx-month__legend"
          role="group"
          aria-labelledby={headingId}
          data-testid="month-legend"
        >
          {paints.map((p) => {
            const shown = !hidden.has(p);
            return (
              <button
                key={p}
                type="button"
                className={`nx-month__chip${shown ? '' : ' is-hidden'}`}
                style={laneVars(p)}
                aria-pressed={shown}
                title={`${shown ? 'Hide' : 'Show'} ${paintLabel(p)}${p === 'linen' ? ' (a linen robot’s delivery)' : ''}`}
                onClick={() => onToggle(p)}
                data-testid={`month-legend-${p}`}
              >
                <span className="nx-month__swatch" aria-hidden="true" />
                {paintLabel(p)}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ---- the month at a glance ---------------------------------------------------------

/** "So far 140 tasks, 6 didn't finish · from today 312 planned"; one half
 *  alone for a month wholly past or wholly to come. */
function monthCounts(days: ReadonlyMap<DateKey, MonthDay>): string {
  let past = 0;
  let pastFail = 0;
  let planned = 0;
  let hasPast = false;
  let hasFuture = false;
  for (const d of days.values()) {
    if (d.kind === 'past') {
      hasPast = true;
      past += d.total;
      pastFail += d.didntFinish;
    } else {
      hasFuture = true;
      planned += d.total;
    }
  }
  const done = `${past} ${past === 1 ? 'task' : 'tasks'}${pastFail ? `, ${didntFinishText(pastFail)}` : ''}`;
  if (!hasFuture) return done;
  if (!hasPast) return `${planned} planned`;
  return `So far ${done} · from today ${planned} planned`;
}

function MonthSummary({
  monthKey,
  timeZone,
  days,
  plan,
  unscheduledOpen,
  onToggleUnscheduled,
  unscheduledRef,
  onChooseDay,
}: {
  monthKey: DateKey;
  timeZone: string;
  days: ReadonlyMap<DateKey, MonthDay>;
  plan: MonthPlanState;
  unscheduledOpen: boolean;
  onToggleUnscheduled: () => void;
  unscheduledRef: RefObject<HTMLButtonElement>;
  onChooseDay: (key: DateKey) => void;
}) {
  const titleId = useId();
  // The summary scrolls as a whole, so it fades its own edges.
  const scrollRef = useRef<HTMLElement>(null);
  const edges = useScrollEdges(scrollRef, [
    days,
    plan.data,
    plan.error,
    plan.loading,
    plan.note,
    unscheduledOpen,
  ]);
  return (
    <section
      ref={scrollRef}
      className={`nx-month__panel${scrollFadeClass(edges)}`}
      aria-labelledby={titleId}
      data-testid="month-summary"
    >
      <div className="nx-month__panel-head">
        <h3 className="nx-month__title" id={titleId}>
          {monthTitle(monthKey)}
        </h3>
      </div>
      <p className="nx-month__line" data-testid="month-summary-counts">
        {monthCounts(days)}
      </p>
      <p className="nx-month__quiet">Choose a day to see it here.</p>

      {plan.shown && (
        <div className="nx-month__plan" data-testid="month-plan">
          <h4 className="nx-month__h">Plan check</h4>
          <MonthPlan
            monthKey={monthKey}
            timeZone={timeZone}
            plan={plan}
            unscheduledOpen={unscheduledOpen}
            onToggleUnscheduled={onToggleUnscheduled}
            unscheduledRef={unscheduledRef}
            onChooseDay={onChooseDay}
          />
        </div>
      )}
      <HelpNote id="tasks.month_view" />
    </section>
  );
}

function PlanNote({ text }: { text: string }) {
  return (
    <p className="nx-month__quiet" data-testid="month-plan-note">
      {text}
    </p>
  );
}

function PlanUnavailable({ plan }: { plan: MonthPlanState }) {
  return (
    <p className="nx-month__line is-warn" role="status" data-testid="month-plan-error">
      Plan check unavailable: {plan.error}.
      {plan.onRetry && (
        <>
          {' '}
          <button
            type="button"
            className="nx-month__link"
            onClick={plan.onRetry}
            data-testid="month-plan-retry"
          >
            Try again
          </button>
        </>
      )}
    </p>
  );
}

function MonthPlan({
  monthKey,
  timeZone,
  plan,
  unscheduledOpen,
  onToggleUnscheduled,
  unscheduledRef,
  onChooseDay,
}: {
  monthKey: DateKey;
  timeZone: string;
  plan: MonthPlanState;
  unscheduledOpen: boolean;
  onToggleUnscheduled: () => void;
  unscheduledRef: RefObject<HTMLButtonElement>;
  onChooseDay: (key: DateKey) => void;
}) {
  const listId = useId();
  const listRef = useRef<HTMLUListElement>(null);
  // Opened, the list is brought into the sidebar's view: at 1280x720 it
  // starts below the fold. Only the sidebar moves while it scrolls on its
  // own, and quietly (`data-tuck-quiet`, as the Agenda opening at now):
  // the shell otherwise read the move as the reader's and tucked the top
  // bar and the Tasks title away (integration shots, 2026-10-31). Below
  // the grid (an upright iPad) the page is what scrolls, so it moves.
  useEffect(() => {
    const list = listRef.current;
    if (!unscheduledOpen || !list) return undefined;
    const box = list.closest<HTMLElement>('.nx-month__panel');
    if (!box || getComputedStyle(box).overflowY === 'visible') {
      list.scrollIntoView?.({ block: 'nearest' });
      return undefined;
    }
    const b = box.getBoundingClientRect();
    const r = list.getBoundingClientRect();
    if (r.bottom <= b.bottom) return undefined;
    box.setAttribute('data-tuck-quiet', '');
    // Its end into view, but never its start out of it.
    box.scrollTop += Math.min(r.bottom - b.bottom, r.top - b.top);
    // The scroll event of a jump arrives a frame later.
    const timer = window.setTimeout(() => box.removeAttribute('data-tuck-quiet'), 200);
    return () => {
      window.clearTimeout(timer);
      box.removeAttribute('data-tuck-quiet');
    };
  }, [unscheduledOpen]);
  if (plan.note) return <PlanNote text={plan.note} />;
  if (plan.error) return <PlanUnavailable plan={plan} />;
  if (!plan.data) {
    // A month wholly in the past is never asked about.
    return plan.loading ? (
      <p className="nx-month__quiet" role="status" data-testid="month-plan-loading">
        Checking the plan…
      </p>
    ) : (
      <p className="nx-month__quiet" data-testid="month-plan-past">
        The plan check covers today and later.
      </p>
    );
  }
  const data = plan.data;
  const summary = planSummary(data, monthKey);
  if (!summary) {
    return (
      <p className="nx-month__quiet" data-testid="month-plan-past">
        The plan check covers today and later.
      </p>
    );
  }
  return (
    <>
      <p
        className={`nx-month__line${summary.shortZones > 0 ? ' is-warn' : ''}`}
        data-testid="month-plan-headline"
      >
        {summary.headline}
      </p>
      <p className="nx-month__quiet" data-testid="month-plan-targets">
        {targetsText(data.targets)}
      </p>
      {data.gaps.length > 0 && (
        <ul className="nx-month__list" data-testid="month-plan-gaps">
          {shortZones(data).map((z) => {
            const first = sameMonth(z.first, monthKey) ? z.first : null;
            return (
              <li key={z.zone}>
                <button
                  type="button"
                  className="nx-month__row-btn"
                  disabled={first === null}
                  onClick={() => first && onChooseDay(first)}
                  title={first ? `Show ${dayShort(first)}` : undefined}
                  data-testid={`month-plan-short-${z.zone}`}
                >
                  <span className="nx-month__zone">{z.zone}</span>
                  <span className="nx-month__row-sub">{shortZoneText(z)}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
      {summary.unscheduledText && (
        <>
          <button
            ref={unscheduledRef}
            type="button"
            className="nx-month__disclosure"
            aria-expanded={unscheduledOpen}
            aria-controls={listId}
            onClick={onToggleUnscheduled}
            data-testid="month-plan-unscheduled-toggle"
          >
            <span aria-hidden="true">{unscheduledOpen ? '▾' : '▸'}</span> {summary.unscheduledText}
          </button>
          {unscheduledOpen && (
            <ul
              ref={listRef}
              className="nx-month__list"
              id={listId}
              data-testid="month-plan-unscheduled"
            >
              {data.unscheduled_zones.map((z) => (
                <li
                  key={z.zone}
                  className="nx-month__zone-row"
                  data-testid={`month-plan-unscheduled-${z.zone}`}
                >
                  <span className="nx-month__zone">{z.zone}</span>
                  <span className="nx-month__row-sub">{unscheduledLine(z, data, timeZone)}</span>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
      {data.zone_source === 'cleans_on_record' && (
        <p className="nx-month__quiet" data-testid="month-plan-zone-source">
          The live zone list could not be read just now, so only zones with cleans on record are
          checked.
        </p>
      )}
      <HelpNote id="tasks.plan_check" />
    </>
  );
}

// ---- the chosen day ----------------------------------------------------------------

function DayPanel({
  day,
  todayKey,
  timeZone,
  robots,
  plan,
  hidden,
  onClose,
  onOpenDay,
  onNewTaskOnDay,
  onShowUnscheduled,
}: {
  day: MonthDay;
  todayKey: DateKey;
  timeZone: string;
  robots: LaneData['robots'];
  plan: MonthPlanState;
  hidden: ReadonlySet<LanePaint>;
  onClose: () => void;
  onOpenDay: () => void;
  onNewTaskOnDay?: () => void;
  onShowUnscheduled: () => void;
}) {
  const titleId = useId();
  const busiest = busiestHours(day.items, day.key, timeZone);
  const services = byService(day.items, robots);
  const failed = day.items.filter((it) => DIDNT_FINISH.has(it.outcome));
  const labelOf = (key: string | null) => robots.find((r) => r.key === key)?.label ?? null;
  // The day's High jobs still to do -- planned, waiting or running -- by
  // time: the day panel lists no other jobs one by one, and these are the
  // ones that go ahead of the rest (2026-10-31). Not the finished ones: a
  // finished job's record (a completion row) carries no priority, so a
  // count of "planned or run" fell as High jobs finished and left past
  // days blank (review PRIO-R6). Said in the heading.
  const high = day.items
    .filter((it) => it.high && STILL_TO_DO.has(it.outcome))
    .sort((a, b) => a.startMs - b.startMs);
  const shortCount = plan.shown && plan.data ? (shortByDay(plan.data).get(day.key) ?? 0) : null;
  const scrollRef = useRef<HTMLDivElement>(null);
  const edges = useScrollEdges(scrollRef, [day, plan.data, plan.error, plan.loading, hidden]);

  const tiles: Array<{ label: string; value: string; tone?: 'fail' | 'warn'; id: string }> =
    day.kind === 'future'
      ? [
          { id: 'planned', label: 'Planned', value: String(day.total) },
          { id: 'busiest', label: 'Busiest', value: busiest ? busiestText(busiest) : '—' },
          ...(shortCount !== null
            ? [
                {
                  id: 'short',
                  label: 'Zones short',
                  value: String(shortCount),
                  tone: shortCount > 0 ? ('warn' as const) : undefined,
                },
              ]
            : []),
        ]
      : [
          {
            id: 'total',
            label: day.kind === 'past' ? 'Tasks' : 'Planned',
            value: String(day.total),
          },
          { id: 'done', label: 'Done', value: String(day.done) },
          {
            id: 'fail',
            label: 'Didn’t finish',
            value: String(day.didntFinish),
            tone: day.didntFinish > 0 ? ('fail' as const) : undefined,
          },
        ];

  // Esc closes the day from anywhere inside it.
  const onKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    if (e.key !== 'Escape') return;
    e.preventDefault();
    onClose();
  };

  return (
    <section
      className="nx-month__panel nx-month__day"
      aria-labelledby={titleId}
      onKeyDown={onKeyDown}
      data-testid="month-day-panel"
    >
      <div className="nx-month__panel-head">
        <h3 className="nx-month__title" id={titleId} data-testid="month-day-title">
          {dayTitle(day.key)}
        </h3>
        <span className="nx-month__tag">
          {day.kind === 'today' ? 'Today' : day.kind === 'past' ? 'What happened' : 'Planned'}
        </span>
        <button
          type="button"
          className="nx-month__close"
          aria-label="Close this day and show the month"
          onClick={onClose}
          data-testid="month-day-close"
        >
          ×
        </button>
      </div>

      <div
        ref={scrollRef}
        className={`nx-month__scroll${scrollFadeClass(edges)}`}
        data-testid="month-day-scroll"
      >
        <div className="nx-month__tiles">
          {tiles.map((t) => (
            <div
              key={t.id}
              className={`nx-month__tile${t.tone ? ` is-${t.tone}` : ''}`}
              data-testid={`month-day-tile-${t.id}`}
            >
              <span className="nx-month__tile-label">{t.label}</span>
              <span className="nx-month__tile-value">{t.value}</span>
            </div>
          ))}
        </div>
        {day.kind !== 'future' && busiest && (
          <p className="nx-month__line" data-testid="month-day-busiest">
            Busiest {busiestText(busiest, true)} · {busiest.count} tasks
          </p>
        )}

        {/* The plan check straight after the figures, ahead of what
            explains it: for the roles it is for it is why the day was
            opened, and at 1280x720 it sat wholly below the fold behind
            "By service" (review, 2026-10-31). */}
        {plan.shown && day.kind !== 'past' && (
          <DayPlan
            dayKey={day.key}
            todayKey={todayKey}
            timeZone={timeZone}
            plan={plan}
            onShowUnscheduled={onShowUnscheduled}
          />
        )}

        {(day.siteBars.length > 0 || hidden.size > 0) && (
          // Its own block: straight after the plan check, the blocked
          // time read as one of its lines.
          <div className="nx-month__block" data-testid="month-day-site-lines">
            {day.siteBars.map((b) => (
              <p
                key={b.id}
                className={`nx-month__line${b.kind === 'fire' ? ' is-fail' : ''}`}
                data-testid={`month-day-site-${b.id}`}
              >
                {siteBarLine(b, day.key, timeZone, day.held)}
              </p>
            ))}
            {hidden.size > 0 && (
              <p className="nx-month__quiet" data-testid="month-day-hidden">
                Not counting what the legend hides.
              </p>
            )}
          </div>
        )}

        {services.length > 0 && (
          <div className="nx-month__block">
            <h4 className="nx-month__h">By service</h4>
            <ul className="nx-month__services" data-testid="month-day-services">
              {services.map((s) => (
                <li key={s.service} data-testid={`month-day-service-${s.service}`}>
                  <span className="nx-month__service-name">{s.label}</span>
                  <span className="nx-month__service-count">
                    {s.total} {s.total === 1 ? 'task' : 'tasks'}
                    {s.didntFinish > 0 ? `, ${didntFinishText(s.didntFinish)}` : ''}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {high.length > 0 && (
          <div className="nx-month__block">
            <h4 className="nx-month__h">High priority, still to do · {high.length}</h4>
            <ul className="nx-month__list" data-testid="month-day-high">
              {high.map((it) => (
                <li
                  key={it.id}
                  className="nx-month__zone-row"
                  data-testid={`month-day-high-${it.id}`}
                >
                  <span>
                    <PriorityBadge lead />
                    <span className="nx-month__time">{formatZonedClock(it.startMs, timeZone)}</span>{' '}
                    {/* Its robot in the lanes' words (`robotText`), as the
                        Didn't finish rows below say it. */}
                    {it.title} · {robotText(it, labelOf)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {failed.length > 0 && (
          <div className="nx-month__block">
            <h4 className="nx-month__h">Didn’t finish</h4>
            <ul className="nx-month__list" data-testid="month-day-failed">
              {failed.map((it) => (
                <FailedRow key={it.id} item={it} timeZone={timeZone} labelOf={labelOf} />
              ))}
            </ul>
          </div>
        )}
      </div>

      <div className="nx-month__actions">
        <button
          type="button"
          className="nx-lane-btn nx-lane-btn--primary"
          onClick={onOpenDay}
          data-testid="month-day-open"
        >
          Open {dayShort(day.key)}
        </button>
        {onNewTaskOnDay && (
          <button
            type="button"
            className="nx-lane-btn"
            onClick={onNewTaskOnDay}
            data-testid="month-day-new-task"
          >
            New task on this day
          </button>
        )}
      </div>
    </section>
  );
}

/** A run or task that didn't finish, with its robot in the lanes' words
 *  (`robotText`, 2026-10-31): the site's name for it, or "Any cleaning
 *  robot" for a run none was pinned to, as every other panel says it. */
function FailedRow({
  item,
  timeZone,
  labelOf,
}: {
  item: LaneItem;
  timeZone: string;
  labelOf: (key: string | null) => string | null;
}) {
  return (
    <li className="nx-month__zone-row" data-testid={`month-day-failed-${item.id}`}>
      <span>
        <span className="nx-month__time">{formatZonedClock(item.startMs, timeZone)}</span>{' '}
        {item.title} · {robotText(item, labelOf)}
      </span>
      {/* The runner's or RMF's own words, as the lanes' panel shows them. */}
      {item.detail && <span className="nx-month__row-sub">{item.detail}</span>}
    </li>
  );
}

function DayPlan({
  dayKey,
  todayKey,
  timeZone,
  plan,
  onShowUnscheduled,
}: {
  dayKey: DateKey;
  todayKey: DateKey;
  timeZone: string;
  plan: MonthPlanState;
  onShowUnscheduled: () => void;
}) {
  const body = (() => {
    if (plan.note) return <PlanNote text={plan.note} />;
    if (plan.error) return <PlanUnavailable plan={plan} />;
    if (!plan.data) {
      return plan.loading ? (
        <p className="nx-month__quiet" role="status">
          Checking the plan…
        </p>
      ) : null;
    }
    if (!isChecked(plan.data, dayKey)) return null;
    const gaps = gapsOn(plan.data, dayKey);
    const unscheduled = plan.data.unscheduled_zones.length;
    return (
      <>
        {gaps.length === 0 ? (
          <p className="nx-month__line" data-testid="month-day-plan-ok">
            Every zone with a clean planned is within its target.
          </p>
        ) : (
          <ul className="nx-month__list" data-testid="month-day-plan">
            {gaps.map((g) => (
              <li
                key={`${g.zone}:${g.from_date}`}
                className="nx-month__plan-line"
                data-testid={`month-day-plan-${g.zone}`}
              >
                {/* The zone's name stands out; the line is `gapLine`'s. */}
                <strong>{g.zone}</strong>
                {gapLine(g, timeZone, todayKey).slice(g.zone.length)}
              </li>
            ))}
          </ul>
        )}
        {unscheduled > 0 && (
          <p className="nx-month__quiet" data-testid="month-day-plan-unscheduled">
            Not counted here: {unscheduled} {unscheduled === 1 ? 'zone has' : 'zones have'} no clean
            planned {planDaysText(plan.data, dayKey)}.{' '}
            <button type="button" className="nx-month__link" onClick={onShowUnscheduled}>
              See {unscheduled === 1 ? 'it' : 'them'}
            </button>
          </p>
        )}
      </>
    );
  })();
  if (!body) return null;
  return (
    <div className="nx-month__block" data-testid="month-day-plan-block">
      <h4 className="nx-month__h">Plan check</h4>
      {body}
    </div>
  );
}
